import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { readingPath } from "~/modules/analytics/lib/loop";

/**
 * The attribution reports.
 *
 * Rows the server marked insignificant say "no conclusion" and show no arrow,
 * even though the delta is right there in the data. Reporting it anyway is the
 * single easiest way to make this product dishonest, and it would work — for
 * about one quarter, until a customer checked.
 */
export function ReflowsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "reflows", { range: filters.range, locale: filters.locale }],
		queryFn: () => api.reflows(entityId, filters),
		...read,
	});

	return (
		<StageShell entityId={entityId} reading="reflows" lede={t("reflow.lede")} filters={filters} setFilters={setFilters}>
			<Async
				state={state}
				empty={
					<EmptyState
						size="compact"
						title={t("reflow.emptyTitle")}
						description={t("reflow.empty")}
						action={<DrillLink to={readingPath(brand(entityId), "published")}>{t("empty.goPublished")}</DrillLink>}
					/>
				}
			>
				{(rows) => (
					<div className="table-scroll">
						<Table variant="resource">
							<TableHeader>
								<TableRow>
									<TableHead className="wrap">{t("table.page")}</TableHead>
									<TableHead className="num">{t("reflow.before")}</TableHead>
									<TableHead className="num">{t("reflow.after")}</TableHead>
									<TableHead className="num">{t("reflow.delta")}</TableHead>
									<TableHead>{t("table.date")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((row) => (
									<TableRow key={row.id}>
										<TableCell className="wrap">
											{/* The page itself, because a reflow is a claim about
											    one page and the first thing to check is what it
											    says. */}
											<DrillLink to={`/w/${entityId}/pages/${row.page_id}`}>{row.page_title}</DrillLink>
											<div className="faint mono">
												{row.before_week} → {row.after_week}
											</div>
										</TableCell>
										<TableCell className="num mono">
											{row.before_share === null ? "—" : fmt.percent(locale, row.before_share)}
										</TableCell>
										<TableCell className="num mono">
											{/* The after figure opens the citations of this page: the
											    reflow is the claim, and they are what it is made of. */}
											<Drill view={{ kind: "citations", label: row.page_title, filters: { page: row.page_id } }}>
												{row.after_share === null ? "—" : fmt.percent(locale, row.after_share)}
											</Drill>
										</TableCell>
										<TableCell className="num">
											{row.significant && row.delta !== null ? (
												<span className={row.delta >= 0 ? "pill pill-success" : "pill pill-danger"}>
													{row.delta >= 0 ? "▲" : "▼"} {fmt.signed(locale, row.delta)}
												</span>
											) : (
												<span className="pill">{t("reflow.notSignificant")}</span>
											)}
										</TableCell>
										<TableCell className="mono">{fmt.date(locale, row.created_at)}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</Async>
		</StageShell>
	);
}
